import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiLock, FiAlertCircle, FiUsers, FiX, FiCopy, FiDownload } = FiIcons;

function RightClickProtection() {
  const [showHint, setShowHint] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Prevent right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      
      // Position des Hinweises relativ zum Viewport setzen
      const x = Math.min(e.clientX - 150, window.innerWidth - 320);
      const y = Math.min(e.clientY - 200, window.innerHeight - 250);
      
      setPosition({ x, y });
      setShowHint(true);
      
      // Automatisch nach 4 Sekunden ausblenden
      const timer = setTimeout(() => {
        setShowHint(false);
      }, 4000);
      
      return () => clearTimeout(timer);
    };

    // Prevent text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Prevent copy
    const handleCopy = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Prevent cut
    const handleCut = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Prevent paste
    const handlePaste = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Prevent drag and drop
    const handleDragStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Prevent print
    const handleBeforePrint = (e) => {
      e.preventDefault();
      return false;
    };

    // Prevent save shortcuts
    const handleKeyDown = (e) => {
      // Prevent Ctrl+S (save)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+C (copy)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+X (cut)
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+V (paste)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+A (select all)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+P (print)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        return false;
      }
      // Prevent F12 (developer tools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+Shift+I (developer tools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        return false;
      }
    };

    // Prevent DevTools
    const handleDevTools = (e) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
        e.preventDefault();
        return false;
      }
    };

    const handleClick = () => {
      setShowHint(false);
    };

    // Add event listeners for copy protection
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('beforeprint', handleBeforePrint);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleDevTools);
    document.addEventListener('click', handleClick);

    // Add CSS to prevent text selection
    const style = document.createElement('style');
    style.innerHTML = `
      * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-touch-callout: none !important;
        -webkit-tap-highlight-color: transparent !important;
      }
      img, svg {
        -webkit-user-drag: none !important;
        -khtml-user-drag: none !important;
        -moz-user-drag: none !important;
        -o-user-drag: none !important;
        user-drag: none !important;
        pointer-events: none !important;
      }
      body {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      @media print {
        body * {
          visibility: hidden !important;
          display: none !important;
        }
        body::before {
          content: "Drucken ist nicht erlaubt!" !important;
          visibility: visible !important;
          display: block !important;
          text-align: center !important;
          font-size: 24px !important;
          margin-top: 100px !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('beforeprint', handleBeforePrint);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleDevTools);
      document.removeEventListener('click', handleClick);
      if (style.parentElement) {
        style.parentElement.removeChild(style);
      }
    };
  }, []);

  return (
    <AnimatePresence>
      {showHint && (
        <motion.div
          className="fixed z-[9999] bg-gray-900 border-2 border-red-500 rounded-xl shadow-2xl p-6 max-w-sm pointer-events-none"
          style={{ 
            left: `${position.x}px`, 
            top: `${position.y}px`,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)'
          }}
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 10 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiLock} className="text-red-500 text-xl flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <SafeIcon icon={FiAlertCircle} className="text-yellow-400 text-lg" />
                <h3 className="text-white font-bold text-lg">Ups! Nicht so schnell!</h3>
              </div>
              
              <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                Das Kopieren, Speichern oder Screenshot-Machen ist hier untersagt. Aber hey, wenn du Fragen zur Website hast...
              </p>
              
              <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiUsers} className="text-red-400 text-lg" />
                  <p className="text-red-400 font-semibold text-sm">
                    TRETE DER KI COMMUNITY BEI!
                  </p>
                </div>
                <p className="text-gray-400 text-xs">
                  Dort bekommst du alle Antworten - und lernst sogar noch was dazu!
                </p>
              </div>
              
              <div className="bg-gray-800/50 rounded p-2 mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiCopy} className="text-gray-400 text-sm" />
                  <SafeIcon icon={FiDownload} className="text-gray-400 text-sm" />
                  <p className="text-gray-500 text-xs font-semibold">Schutz aktiv:</p>
                </div>
                <p className="text-gray-600 text-xs">
                  Kopieren • Screenshot • Drucken • Rechtsklick • Auswahl
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <a 
                  href="https://monkeymedia.at/go/kicommunity" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 text-xs underline underline-offset-2 pointer-events-auto"
                >
                  Zur KI Community →
                </a>
                <p className="text-gray-500 text-xs">
                  Klick zum Schließen
                </p>
              </div>
            </div>
          </div>
          
          {/* Animated border effect */}
          <div className="absolute inset-0 rounded-xl border-2 border-red-500/30 pointer-events-none animate-pulse" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default RightClickProtection;